//
// # IDE Entry Point
//
// The webpack entry point for running the IDE in a browser.
// Creates a Workspace, connects to AlphaServer, and renders the React app.
//

import React from "react";
import { createRoot } from "react-dom/client";
import { Result, type GitInfo } from "CoreUtils";
import "./config"; // Window.__GENALPHA_CONFIG__ global type declaration
import { Ident } from "GenAlphaModel";
import { AlphaClient, IdentValidator } from "./client";
import { Workspace } from "./workspace";
import { App } from "./App";
import {
  initFirebase,
  completeSignIn,
  getCurrentToken,
  getCurrentUser,
  onAuthStateChange,
  signOut,
} from "./firebaseAuth";
import { LoginScreen } from "./components/LoginScreen";
import { initBrowserLog, logFromBrowser } from "./browserLog";

// Definitions made at compile time
export interface CompilerDefs {
  GIT_INFO: GitInfo;
  ALPHA_SERVER_URL: string;
}
declare global {
  const COMPILER_DEFS: CompilerDefs;
}

async function main(): Promise<void> {
  // When served from AlphaServer (cloud), ALPHA_SERVER_URL is empty - use same origin.
  // When running standalone dev server, it points to localhost:3000.
  const alphaServerUrl =
    COMPILER_DEFS.ALPHA_SERVER_URL || window.location.origin;

  // Wire the browser-log endpoint to the same AlphaServer base URL the
  // rest of the IDE uses. Must run before any `logFromBrowser` call.
  initBrowserLog(alphaServerUrl);

  console.log("Starting GenAlpha IDE at:");
  console.log(new Date().toString());
  console.log("Git:", COMPILER_DEFS.GIT_INFO.commitHash);

  // Create the root DOM element
  const rootDiv = document.createElement("div");
  rootDiv.id = "root";
  rootDiv.style.width = "100vw";
  rootDiv.style.height = "100vh";
  rootDiv.style.margin = "0";
  rootDiv.style.padding = "0";
  rootDiv.style.overflow = "hidden";
  document.body.style.margin = "0";
  document.body.appendChild(rootDiv);
  const root = createRoot(rootDiv);

  // Initialize Firebase if config is available
  const firebaseConfig = window.__GENALPHA_CONFIG__?.firebase;
  // Auth error message to persist across re-renders (e.g., "not allowed")
  let authError: string | undefined;
  // Guard against concurrent setupIde calls (e.g., rapid onAuthStateChanged fires)
  let setupInProgress = false;
  if (firebaseConfig) {
    initFirebase(firebaseConfig);

    // Check if we're returning from an email sign-in link. If the link is
    // expired, already consumed, or otherwise rejected by Firebase,
    // `completeSignIn` rejects — wrap with `Result.wrapAsync` so the
    // rejection becomes a value we can branch on, and we still fall through
    // to rendering the login screen (with a user-facing error) instead of
    // leaving the page blank.
    const signInResult = await Result.wrapAsync(completeSignIn)();
    if (signInResult.err) {
      authError =
        "Sign-in link is invalid or expired. Please request a new one.";
      const reason =
        signInResult.val instanceof Error
          ? signInResult.val.message
          : String(signInResult.val);
      logFromBrowser(
        "auth-link-failed",
        "email-link sign-in failed during completeSignIn",
        { reason }
      );
    }

    // Always listen for auth state changes (sign-in, sign-out, token refresh).
    // This handles both the initial "no user" case and re-renders after
    // sign-out (e.g., when the server rejects a user with 403).
    onAuthStateChange((user) => {
      if (user) {
        if (setupInProgress) return;
        setupInProgress = true;
        authError = undefined;
        void setupIde(root, rootDiv, alphaServerUrl, (msg) => {
          authError = msg;
        }).finally(() => {
          setupInProgress = false;
        });
      } else {
        root.render(
          <LoginScreen errorMessage={authError} key={authError ?? "no-error"} />
        );
      }
    });

    // If no user yet, show login screen immediately (don't wait for callback).
    // Pass `authError` so a failed completeSignIn surfaces on first paint
    // rather than flashing a clean login screen before the auth-state
    // listener re-renders with the error.
    if (!getCurrentUser()) {
      root.render(
        <LoginScreen errorMessage={authError} key={authError ?? "no-error"} />
      );
    }

    return;
  }

  // Auth is not configured - proceed to IDE without authentication
  void setupIde(root, rootDiv, alphaServerUrl);
}

// Set up the IDE after authentication is resolved
async function setupIde(
  root: ReturnType<typeof createRoot>,
  rootDiv: HTMLDivElement,
  alphaServerUrl: string,
  onAuthError?: (message: string) => void
): Promise<void> {
  //
  // FIXME: make this a real UI element.
  // We should continue trying to connect forever,
  // and have a UI widget that says when disconnected.
  //
  // Connect to AlphaServer
  console.log(`Connecting to AlphaServer at ${alphaServerUrl}...`);
  const clientResult = await AlphaClient.create({
    alphaServerUrl,
    getToken: getCurrentToken,
    userEmail: getCurrentUser()?.email ?? undefined,
  });
  if (clientResult.err) {
    console.error("Failed to connect to AlphaServer:", clientResult.val);
    const errData = clientResult.val as { data?: { why?: string } };

    // If rejected by allowlist, sign out and show login with error
    if (errData.data?.why === "not-allowed") {
      const userEmail = getCurrentUser()?.email ?? "your email";
      const msg = `Access denied: ${userEmail} is not authorized. Please sign in with an authorized account.`;
      onAuthError?.(msg);
      await signOut();
      // Render directly in case onAuthStateChange doesn't fire
      root.render(<LoginScreen errorMessage={msg} key={msg} />);
      return;
    }

    rootDiv.innerHTML = `
      <div style="padding: 20px; font-family: monospace;">
        <h2>Failed to connect to AlphaServer</h2>
        <p>Make sure AlphaServer is running at ${alphaServerUrl}</p>
        <pre>${JSON.stringify(clientResult.val, null, 2)}</pre>
      </div>
    `;
    return;
  }

  const client = clientResult.val;
  console.log(`Connected to AlphaServer as client: ${client.clientId}`);

  // Create the workspace with the client
  const workspace = new Workspace(client);

  // Wire up design-open events so the IDE opens a new tab when the LM opens a design
  client.onDesignOpen = (msg) => {
    const identResult = IdentValidator.validator.validate(msg.ident);
    if (identResult.err) {
      console.error(
        `Invalid ident in design-open event: ${identResult.val.why}`
      );
      return;
    }
    const ident = identResult.val;
    void workspace.handleOpenRequest(ident).then(() => {
      if (msg.dirty) {
        workspace.markDirty(ident);
      }
    });
  };

  // Wire up design-load events so editor canvas refreshes after state changes.
  // For multi-user collaboration, other users' changes trigger design-load events
  // to reload the design content (simpler than incremental updates).
  client.onDesignLoad = (msg) => {
    void workspace.handleDesignLoad(msg);
  };

  // Wire up incremental design-changes events: apply streamed changes live to
  // the canvas (LM/agent + GUI edits) without a full reload.
  client.onDesignChanges = (msg) => {
    void workspace.handleDesignChanges(msg);
  };

  // Wire up agent-tree pushes so the Agents panel reflects the live hierarchy.
  client.onAgentTree = (msg) => {
    workspace.setAgentTree(msg.tree);
  };

  // Wire up LVS result events so the LVS panel displays verification results
  client.onLvsResult = (msg) => {
    // Validate and stringify the ident so we can check it matches the active layout
    const identResult = IdentValidator.validator.validate(msg.ident);
    const identKey = identResult.ok
      ? Ident.stringify(identResult.val)
      : undefined;

    workspace.setLvsResult({
      ...msg.result,
      identKey,
    });
  };

  // Wire up DRC result events so the DRC panel displays verification results
  client.onDrcResult = (msg) => {
    // Validate and stringify the ident so we can check it matches the active layout
    const identResult = IdentValidator.validator.validate(msg.ident);
    const identKey = identResult.ok
      ? Ident.stringify(identResult.val)
      : undefined;

    workspace.setDrcResult({
      ...msg.result,
      identKey,
    });
  };

  // Wire up IVA result events so the IVA panel displays verification results
  client.onIvaResult = (msg) => {
    const identResult = IdentValidator.validator.validate(msg.ident);
    const identKey = identResult.ok
      ? Ident.stringify(identResult.val)
      : undefined;

    workspace.setIvaResult({
      ...msg.result,
      identKey,
    });
  };

  // Wire up SRV result events so the SRV panel and canvas overlay display
  // Schematic Rules Validation results pushed from the server.
  client.onSrvResult = (msg) => {
    workspace.srv.applySrvResult(msg.result);
  };

  // Wire up canvas capture for the get-img tool
  client.onCaptureCanvas = () => workspace.captureCanvas();

  // Wire up depot tree change events so DesignExplorer refreshes
  client.onDesignTreeChanged = (kind) => {
    workspace.handleDesignTreeChanged(kind);
  };

  // Wire up WebSocket connection state changes to drive the title-bar
  // network status indicator.
  client.onConnectionStateChanged = (isConnected) => {
    workspace.setConnected(isConnected);
  };

  // Wire up the richer connection lifecycle events to the in-app console
  // panel so users (and we, during debugging) can see exactly when and why
  // the WebSocket goes up and down. This is the primary tool for diagnosing
  // zombie-connection issues after machine sleep.
  //
  // Initial "connected" fires once; subsequent opens are reported as
  // "reconnected" with downtime and attempt counts. "disconnected" carries
  // the WS close code and reason. All three are also mirrored to DevTools
  // console by logToConsole() for cross-reference.
  client.onConnectionEvent = (event) => {
    switch (event.kind) {
      case "connected":
        workspace.logToConsole(
          "info",
          "network",
          `Connected — clientId=${event.clientId} url=${event.url}`
        );
        break;
      case "disconnected":
        workspace.logToConsole(
          "error",
          "network",
          `Disconnected — clientId=${event.clientId} code=${
            event.code
          } reason="${event.reason || "none"}" durationMs=${
            event.durationMs
          } wasStable=${event.wasStable}`
        );
        break;
      case "reconnected":
        workspace.logToConsole(
          "info",
          "network",
          `Reconnected — clientId=${event.clientId} url=${event.url} downtimeMs=${event.downtimeMs} attempts=${event.attempts}`
        );
        break;
    }
  };

  // Show the "Connection Lost — Reload?" modal when AlphaClient has decided
  // the WebSocket cannot be recovered in place. This is a terminal state for
  // the current page; the user's only resolution is to reload.
  client.onConnectionLost = (reason) => {
    workspace.logToConsole(
      "error",
      "network",
      `Connection lost — reason=${reason}. Prompting user to reload.`
    );
    workspace.showReconnectDialog(reason);
  };

  // After a transient reconnect where the server rebuilt our ClientContext,
  // re-open the open designs so the server re-adopts the still-alive shared
  // state (grace window) instead of leaving the tabs disconnected.
  client.onReconnectResume = () => {
    workspace.logToConsole(
      "info",
      "network",
      "Reconnected after client-context loss — resuming open designs."
    );
    void workspace.resumeOpenDesignsAfterReconnect();
  };

  // Mount the React app
  root.render(<App workspace={workspace} />);
}

// Run main
main().catch((err) => {
  console.error("IDE startup failed:", err);
});
